import { MetricValue } from 'dashboard409a/services/types';

// Format constants
const FORMAT_PERCENT = '%';
const FORMAT_MULTIPLE = 'x';

// Metric value keys
const METRIC_KEY = {
  ENTERPRISE_VALUE: 'enterprise_value',
  LTM_REVENUE: 'ltm_revenue',
  NTM_REVENUE: 'ntm_revenue',
  LTM_REVENUE_GROWTH: 'ltm_revenue_growth',
  GROSS_MARGIN: 'gross_margin',
  LTM_EBITDA: 'ltm_ebitda',
  LTM_EBITDA_MARGIN: 'ltm_ebitda_margin',
  EV_DIVIDED_BY_LTM_REVENUE: 'ev_divided_by_ltm_revenue',
  EV_DIVIDED_BY_NTM_REVENUE: 'ev_divided_by_ntm_revenue',
  EV_DIVIDED_BY_LTM_EBITDA: 'ev_divided_by_ltm_ebitda',
  EV_DIVIDED_BY_NTM_EBITDA: 'ev_divided_by_ntm_ebitda',
};

// Metric display names
const METRIC_NAME = {
  EV_NTM_REVENUE: 'EV/NTM Revenue',
  EV_LTM_REVENUE: 'EV/LTM Revenue',
  ENTERPRISE_VALUE: 'Enterprise Value',
  GROSS_MARGIN: 'Gross Margin',
  EV_LTM_EBITDA: 'EV/LTM EBITDA',
  EV_NTM_EBITDA: 'EV/NTM EBITDA',
  LTM_REVENUE: 'LTM Revenue',
  LTM_REVENUE_GROWTH: 'LTM Revenue Growth %',
  LTM_EBITDA: 'LTM EBITDA',
  LTM_EBITDA_MARGIN: 'LTM EBITDA Margin',
  LTM_GROSS_MARGIN: 'LTM Gross Margin',
};

// Start Metric configuration for Multiples and Performance Trends tables
export const METRICS_OPTIONS = [
  METRIC_NAME.EV_NTM_REVENUE,
  METRIC_NAME.EV_LTM_REVENUE,
  METRIC_NAME.ENTERPRISE_VALUE,
  METRIC_NAME.GROSS_MARGIN
];

export const MULTIPLES_OPTIONS = [
  METRIC_NAME.EV_LTM_REVENUE,
  METRIC_NAME.EV_NTM_REVENUE,
  METRIC_NAME.EV_LTM_EBITDA,
  METRIC_NAME.EV_NTM_EBITDA
];

export const PERFORMANCE_TRENDS_OPTIONS = [
  METRIC_NAME.ENTERPRISE_VALUE,
  METRIC_NAME.LTM_REVENUE,
  METRIC_NAME.LTM_REVENUE_GROWTH,
  METRIC_NAME.LTM_EBITDA,
  METRIC_NAME.LTM_EBITDA_MARGIN,
  METRIC_NAME.LTM_GROSS_MARGIN
];

export interface MetricConfig {
  symbol: string;
  currentVal: string;
  default: string;
}

export const gpcMetrics: { [key: string]: MetricConfig } = {
  [METRIC_NAME.EV_NTM_REVENUE]: {
    symbol: FORMAT_MULTIPLE,
    currentVal: METRIC_KEY.NTM_REVENUE,
    default: METRIC_KEY.EV_DIVIDED_BY_NTM_REVENUE,
  },
  [METRIC_NAME.EV_LTM_REVENUE]: {
    symbol: FORMAT_MULTIPLE,
    currentVal: METRIC_KEY.LTM_REVENUE,
    default: METRIC_KEY.EV_DIVIDED_BY_LTM_REVENUE,
  },
  [METRIC_NAME.ENTERPRISE_VALUE]: {
    symbol: FORMAT_PERCENT,
    currentVal: METRIC_KEY.ENTERPRISE_VALUE,
    default: METRIC_KEY.ENTERPRISE_VALUE,
  },
  [METRIC_NAME.GROSS_MARGIN]: {
    symbol: FORMAT_PERCENT,
    currentVal: METRIC_KEY.GROSS_MARGIN,
    default: METRIC_KEY.GROSS_MARGIN,
  },
  [METRIC_NAME.EV_LTM_EBITDA]: {
    symbol: FORMAT_MULTIPLE,
    currentVal: METRIC_KEY.EV_DIVIDED_BY_LTM_EBITDA,
    default: METRIC_KEY.EV_DIVIDED_BY_LTM_EBITDA,
  },
  [METRIC_NAME.EV_NTM_EBITDA]: {
    symbol: FORMAT_MULTIPLE,
    currentVal: METRIC_KEY.EV_DIVIDED_BY_NTM_EBITDA,
    default: METRIC_KEY.EV_DIVIDED_BY_NTM_EBITDA,
  },
  [METRIC_NAME.LTM_REVENUE]: {
    symbol: FORMAT_PERCENT,
    currentVal: METRIC_KEY.LTM_REVENUE,
    default: METRIC_KEY.LTM_REVENUE,
  },
  [METRIC_NAME.LTM_REVENUE_GROWTH]: {
    symbol: FORMAT_PERCENT,
    currentVal: METRIC_KEY.LTM_REVENUE_GROWTH,
    default: METRIC_KEY.LTM_REVENUE_GROWTH,
  },
  [METRIC_NAME.LTM_EBITDA]: {
    symbol: FORMAT_PERCENT,
    currentVal: METRIC_KEY.LTM_EBITDA,
    default: METRIC_KEY.LTM_EBITDA,
  },
  [METRIC_NAME.LTM_EBITDA_MARGIN]: {
    symbol: FORMAT_PERCENT,
    currentVal: METRIC_KEY.LTM_EBITDA_MARGIN,
    default: METRIC_KEY.LTM_EBITDA_MARGIN,
  },
  [METRIC_NAME.LTM_GROSS_MARGIN]: {
    symbol: FORMAT_PERCENT,
    currentVal: METRIC_KEY.GROSS_MARGIN,
    default: METRIC_KEY.GROSS_MARGIN,
  },
};
// End Metric configuration for Multiples and Performance Trends tables

// Start Median Metric Card definitions
export interface MetricCardConfig {
  id: string;
  title: string;
  format: string;
  valueKey: string;
}

export const METRIC_CARD = {
  NTM_REVENUE_MULTIPLE: 'ntmRevenueMultiple',
  NTM_REVENUE_GROWTH: 'ntmRevenueGrowth',
  GROSS_MARGIN: 'grossMargin',
  LTM_REVENUE_MULTIPLE: 'ltmRevenueMultiple',
  LTM_REVENUE_GROWTH: 'ltmRevenueGrowth',
  EBITDA_MARGIN: 'ebitdaMargin',
};

export const METRIC_CARD_CONFIG: MetricCardConfig[] = [
  { 
    id: METRIC_CARD.NTM_REVENUE_MULTIPLE,
    title: METRIC_NAME.EV_NTM_REVENUE, 
    format: FORMAT_MULTIPLE,
    valueKey: METRIC_CARD.NTM_REVENUE_MULTIPLE
  },
  { 
    id: METRIC_CARD.NTM_REVENUE_GROWTH,
    title: METRIC_NAME.LTM_REVENUE_GROWTH, 
    format: FORMAT_PERCENT,
    valueKey: METRIC_CARD.NTM_REVENUE_GROWTH
  },
  { 
    id: METRIC_CARD.GROSS_MARGIN,
    title: METRIC_NAME.GROSS_MARGIN, 
    format: FORMAT_PERCENT,
    valueKey: METRIC_CARD.GROSS_MARGIN
  },
  { 
    id: METRIC_CARD.LTM_REVENUE_MULTIPLE,
    title: METRIC_NAME.EV_LTM_REVENUE, 
    format: FORMAT_MULTIPLE,
    valueKey: METRIC_CARD.LTM_REVENUE_MULTIPLE
  },
  { 
    id: METRIC_CARD.LTM_REVENUE_GROWTH,
    title: METRIC_NAME.LTM_REVENUE_GROWTH, 
    format: FORMAT_PERCENT,
    valueKey: METRIC_CARD.LTM_REVENUE_GROWTH
  },
  { 
    id: METRIC_CARD.EBITDA_MARGIN,
    title: METRIC_NAME.LTM_EBITDA_MARGIN, 
    format: FORMAT_PERCENT,
    valueKey: METRIC_CARD.EBITDA_MARGIN
  },
];

export const createMetricCards = (values: Record<string, MetricValue>) => 
  METRIC_CARD_CONFIG.map(config => ({
    id: config.id,
    title: config.title,
    format: config.format,
    value: values[config.valueKey]
  }));

export const METRIC_CARDS = (
  ntmRevenueMultiple: MetricValue, 
  ntmRevenueGrowth: MetricValue, 
  grossMargin: MetricValue, 
  ltmRevenueMultiple: MetricValue, 
  ltmRevenueGrowth: MetricValue, 
  ebitdaMargin: MetricValue
) => createMetricCards({
  [METRIC_CARD.NTM_REVENUE_MULTIPLE]: ntmRevenueMultiple,
  [METRIC_CARD.NTM_REVENUE_GROWTH]: ntmRevenueGrowth,
  [METRIC_CARD.GROSS_MARGIN]: grossMargin,
  [METRIC_CARD.LTM_REVENUE_MULTIPLE]: ltmRevenueMultiple,
  [METRIC_CARD.LTM_REVENUE_GROWTH]: ltmRevenueGrowth,
  [METRIC_CARD.EBITDA_MARGIN]: ebitdaMargin,
});
// End Median Metric Card definitions